import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DrawerComponent } from "../../../_metronic/assets/ts/components";
import CalendlyWidget from "./CalendlyWidget/CalendlyWidget";
import { usePortal } from "../../../context/PortalContext";
import useAppointmentBookingCalendar from "../../../hooks/useAppointmentBookingCalendar";
import {
  getBookingDrawerState,
  subscribeToBookingDrawerState,
} from "../../../helpers/drawerHelpers";
import type { IPrefill } from "./CalendlyWidget/CalendlyWidget";
import type { BookingCalendarType } from "../../../hooks/useAppointmentBookingCalendar";

type BookingStatus = "idle" | "setting" | "ready";

type DrawerSession = {
  calendarUrl: string;
  prefill?: IPrefill;
  calendarType?: BookingCalendarType;
};

export const Booking = () => {
  const { clientData } = usePortal();
  const {
    isLoading: isCalendarConfigLoading,
    selectedCalendarUrl,
    prefill,
    selectedCalendarType,
  } = useAppointmentBookingCalendar();
  const [bookingStatus, setBookingStatus] = useState<BookingStatus>("idle");
  const [bookingDrawerState, setBookingDrawerState] = useState(
    getBookingDrawerState()
  );
  const [drawerSession, setDrawerSession] = useState<DrawerSession | null>(
    null
  );
  const latestCalendarRef = useRef<DrawerSession | null>(null);

  latestCalendarRef.current = selectedCalendarUrl
    ? {
      calendarUrl: selectedCalendarUrl,
      prefill: prefill.client,
      calendarType: selectedCalendarType,
    }
    : null;

  useEffect(() => {
    return subscribeToBookingDrawerState(() => {
      setBookingDrawerState(getBookingDrawerState());
    });
  }, []);

  useEffect(() => {
    DrawerComponent.createInstances("#booking_drawer");
    const drawer = DrawerComponent.getInstance("booking_drawer");

    drawer?.on("kt.drawer.shown", () => {
      const latestCalendar = latestCalendarRef.current;

      if (!latestCalendar) {
        return;
      }

      setBookingStatus("idle");
      setDrawerSession(latestCalendar);
    });

    drawer?.on("kt.drawer.after.hidden", () => {
      setBookingStatus("idle");
      setDrawerSession(null);
    });
  }, []);

  const calendarType = drawerSession?.calendarType ?? selectedCalendarType;

  if (!clientData) {
    return null;
  }

  return (
    <div className="d-flex flex-column gap-10">
      <div className="d-flex flex-column gap-2">
        <h3 className="text-left">
          Speak with
          {calendarType === "adviser"
            ? " an Independent Financial Adviser"
            : " a Retirement Specialist"}
        </h3>
        <span className="text-gray-800 fs-5">
          Book a free phone consultation with
          {calendarType === "adviser"
            ? " an Independent Financial Adviser"
            : " a Retirement Specialist"}
          discuss your options and get a personalised recommendation.
        </span>
        {bookingStatus !== "idle" && (
          <div className="d-flex flex-column gap-4 align-items-center justify-content-center p-6">
            {bookingStatus === "setting" ? (
              <>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="loading-text">Setting booking...</div>
              </>
            ) : (
              <div className="d-flex flex-column flex-sm-row gap-4 align-items-center justify-content-center">
                {bookingDrawerState.config.completionActions?.map((action) => (
                  <Link
                    key={`${action.to}-${action.label}`}
                    to={action.to}
                    className={action.className ?? "btn btn-primary"}
                  >
                    {action.iconClassName && (
                      <i className={action.iconClassName}></i>
                    )}
                    {action.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="d-flex flex-column flex-md-row gap-10 align-items-center justify-content-center card card-rounded">
        <div className="d-flex w-100h-100 p-6 justify-content-center">
          <div className="d-flex flex-column flex-grow-1 gap-5">
            {drawerSession ? (
              <CalendlyWidget
                key={drawerSession.calendarUrl}
                url={drawerSession.calendarUrl}
                prefill={drawerSession.prefill}
                onApiUpdateStart={() => setBookingStatus("setting")}
                onApiUpdate={() => setBookingStatus("ready")}
              />
            ) : (
              <div className="calendly-inline-widget-container">
                <div className="calendly-loading-overlay">
                  <div className="loading-content">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="loading-text">
                      {!selectedCalendarUrl && !isCalendarConfigLoading
                        ? "Appointment scheduler unavailable."
                        : "Loading appointment scheduler..."}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="card border rounded border-white p-10 mt-5">
        <h1 className="fs-1">Need to get in touch now?</h1>
        <p className="text-dark">
          If you don't want to arrange an appointment, feel free to give us a
          call about this quote or any other questions you have:
        </p>
        <h1>
          <a
            href="tel:08082812558"
            className="text-primary text-decoration-underline"
          >
            0808 281 2558
          </a>
        </h1>
        <p className="text-dark">
          Mon to Fri - 9am - 5pm <br />
          Sat - 9am - 1pm
        </p>
      </div>
    </div>
  );
};
